<template>
  <div id="orders-visibility-block">
    <condition-builder
      class="mt-2"
      :value="value"
      :fields="fieldItems"
      :operators="operatorItems"
      :has-error="!!errorKey"
      :label="$t('settings.ordersVisibility.title')"
      :subtitle="$t('settings.ordersVisibility.description')"
      reset-value-on-field-change
      collapse-multi-value-to-first
      @input="$emit('input', $event)"
    >
      <template #toolbar>
        <v-btn
          icon
          small
          class="mr-2"
          :aria-label="$t('settings.ordersVisibility.help.title')"
          @click="helpDialog = true"
        >
          <v-icon color="primary">mdi-information-outline</v-icon>
        </v-btn>
        <v-btn color="primary" outlined small class="mr-2" @click="resetDialog = true">
          <v-icon left small>mdi-restore</v-icon>
          {{ $t('settings.ordersVisibility.reset') }}
        </v-btn>
      </template>

      <template #empty>
        <p class="text-body-2 font-italic">{{ $t('settings.ordersVisibility.noBlock') }}</p>
      </template>

      <template #block-separator>
        <div class="text-center font-weight-bold my-1">{{ $t('settings.ordersVisibility.or') }}</div>
      </template>

      <template #block-header="{ index }">
        <v-card-subtitle class="pl-2">
          {{ $t('settings.ordersVisibility.block', { index: index + 1 }) }}
        </v-card-subtitle>
      </template>

      <!-- Constrained field selector (avoids free-text that would break the API contract). -->
      <template #field="{ condition, errors, update }">
        <v-select
          :value="condition.field"
          dense
          :items="fieldItems"
          :label="$t('settings.ordersVisibility.field')"
          hide-details="auto"
          :error-messages="errors"
          @change="update($event)"
        />
      </template>

      <!-- Field-typed value editor: location fields resolve ONLY to the CURRENT_USER_LOCATION
           variable — the config is tenant-wide, so pinning a specific static store would apply
           to every user regardless of their own store (meaningless). Statuses use the status list.
           `isMultiValue` comes from the builder's own operator-arity classification (single source). -->
      <template #value="{ condition, isMultiValue, update }">
        <validation-provider
          v-if="condition.field"
          class="width-100"
          rules="required"
          :name="$t('settings.ordersVisibility.value')"
          v-slot="{ errors }"
        >
          <v-select
            v-if="isLocationField(condition.field)"
            key="location-value"
            :value="condition.values_to_check"
            dense
            :items="locationValueItems"
            :label="$t('settings.ordersVisibility.value')"
            hide-details="auto"
            :multiple="isMultiValue"
            :error-messages="errors"
            @change="update($event)"
          >
            <!-- The value is the dynamic CURRENT_USER_LOCATION variable (per PO mockup): the
                 selected tag is blue (colour = variable), and the "var" badge lives on the
                 dropdown option. The raw token is documented in the help modal, not inline. -->
            <template #selection="{ item }">
              <v-chip small color="blue lighten-5" text-color="blue darken-4">{{ item.text }}</v-chip>
            </template>
            <template #item="{ item }">
              <span>{{ item.text }}</span>
              <v-spacer />
              <span class="ov-var-badge">var</span>
            </template>
          </v-select>
          <v-select
            v-else
            key="status-value"
            :value="condition.values_to_check"
            dense
            :items="statusItems"
            :label="$t('settings.ordersVisibility.value')"
            hide-details="auto"
            :multiple="isMultiValue"
            :error-messages="errors"
            @change="update($event)"
          />
        </validation-provider>
        <v-text-field
          v-else
          disabled
          dense
          :label="$t('settings.ordersVisibility.selectFieldFirst')"
          hide-details="auto"
        />
      </template>
    </condition-builder>

    <p v-if="errorKey" role="alert" class="error--text text-body-2 px-4 pb-2">
      {{ $t(errorKey) }}
    </p>

    <condition-builder-help-dialog
      :value="helpDialog"
      :title="$t('settings.ordersVisibility.help.title')"
      :fields="fieldItems"
      :operators="helpOperators"
      :variables="helpVariables"
      @input="helpDialog = $event"
    />

    <confirmation-dialog
      :value="resetDialog"
      :title="$t('settings.ordersVisibility.resetConfirmTitle')"
      :text="$t('settings.ordersVisibility.resetConfirmText')"
      @yes="resetToDefault"
      @no="resetDialog = false"
      @input="resetDialog = $event"
    />
  </div>
</template>

<script>
  import { ValidationProvider } from 'vee-validate'
  import ConditionBuilder from '../ConditionBuilder.vue'
  import ConditionBuilderHelpDialog from '../ConditionBuilderHelpDialog.vue'
  import ConfirmationDialog from '../ConfirmationDialog.vue'
  import {
    CURRENT_USER_LOCATION,
    ORDER_VISIBILITY_FIELDS,
    ORDER_VISIBILITY_OPERATORS,
    FIELD_LABEL_KEYS,
    ORDER_STATUS_OPTIONS,
    DEFAULT_ORDERS_VISIBILITY_CONDITIONS,
    isMultiValueOperator,
    isLocationField,
    cloneConditions,
  } from '../../helpers/orderVisibility'

  export default {
    name: 'OrderVisibilitySettings',
    components: {
      conditionBuilder: ConditionBuilder,
      conditionBuilderHelpDialog: ConditionBuilderHelpDialog,
      confirmationDialog: ConfirmationDialog,
      'validation-provider': ValidationProvider,
    },
    props: {
      value: {
        type: Array,
        default: () => [],
      },
      defaultConditions: {
        type: Array,
        default: () => [],
      },
      /** i18n key of the current business-validation error, or '' when valid. */
      errorKey: {
        type: String,
        default: '',
      },
    },
    data() {
      return {
        helpDialog: false,
        resetDialog: false,
      }
    },
    computed: {
      fieldItems() {
        return ORDER_VISIBILITY_FIELDS.map((field) => ({ text: this.$t(FIELD_LABEL_KEYS[field]), value: field }))
      },
      operatorItems() {
        return ORDER_VISIBILITY_OPERATORS.map((operator) => ({ text: this.$t(`trigger.${operator}`), value: operator }))
      },
      statusItems() {
        const known = ORDER_STATUS_OPTIONS.map((status) => ({ text: this.$t(status.labelKey), value: status.value }))
        // The back-end does not constrain the status enum, so a stored value can fall
        // outside ORDER_STATUS_OPTIONS (legacy data, test data, or a status the front does
        // not know yet). Inject those as extra items — labelled "(unknown)" — so they stay
        // visible and editable in the constrained select instead of silently vanishing.
        const unknownItems = this.unknownStatusValues.map((value) => ({
          text: this.$t('settings.ordersVisibility.unknownStatusLabel', { value }),
          value,
        }))
        return [...known, ...unknownItems]
      },
      unknownStatusValues() {
        const knownValues = new Set(ORDER_STATUS_OPTIONS.map((status) => status.value))
        const unknown = new Set()
        const blocks = Array.isArray(this.value) ? this.value : []
        blocks.forEach((block) => {
          const conditions = Array.isArray(block) ? block : []
          conditions.forEach((condition) => {
            if (condition?.field !== 'current_status') {
              return
            }
            const raw = condition.values_to_check
            const values = Array.isArray(raw) ? raw : [raw]
            values.forEach((value) => {
              if (value != null && value !== '' && !knownValues.has(value)) {
                unknown.add(value)
              }
            })
          })
        })
        return [...unknown]
      },
      locationValueItems() {
        // Location fields can only reference the connected user's store. The config is
        // tenant-wide, so pinning a specific static store would apply it to every user
        // regardless of their own store — meaningless. Only the dynamic variable is offered,
        // shown by its human label; the blue "var" cue marks it as dynamic. Its raw token
        // (CURRENT_USER_LOCATION) and description live in the help modal.
        return [{ text: this.$t('settings.ordersVisibility.currentUserLocation'), value: CURRENT_USER_LOCATION }]
      },
      helpOperators() {
        return this.operatorItems.map((operator) => ({ ...operator, multiValue: isMultiValueOperator(operator.value) }))
      },
      helpVariables() {
        return [
          {
            text: this.$t('settings.ordersVisibility.currentUserLocation'),
            value: CURRENT_USER_LOCATION,
            description: this.$t('settings.ordersVisibility.help.currentUserLocationDescription'),
          },
        ]
      },
    },
    methods: {
      isLocationField,
      resetToDefault() {
        const source = this.defaultConditions.length ? this.defaultConditions : DEFAULT_ORDERS_VISIBILITY_CONDITIONS
        // Emit a fresh clone; ConditionBuilder rebuilds its internal model from the new value.
        this.$emit('input', cloneConditions(source))
        this.resetDialog = false
      },
    },
  }
</script>

<style scoped>
  .width-100 {
    width: 100%;
  }
  /* Contextual-variable value (CURRENT_USER_LOCATION), per PO mockup: the dropdown option
     carries a light-blue "var" badge. The selected chip itself uses Vuetify colour props
     (blue lighten-5 / blue darken-4) so it stays readable in both light and dark themes. */
  .ov-var-badge {
    display: inline-flex;
    align-items: center;
    padding: 1px 6px;
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
    color: #185fa5 !important;
    background: #e6f1fb;
    border-radius: 3px;
  }
  /* Mobile only (xs < 600px): shrink the condition inputs' text so long values/labels
     fit better and read less oversized. Scoped to this block — no impact on Triggers/Lists. */
  @media (max-width: 599.98px) {
    #orders-visibility-block ::v-deep .v-select__selection,
    #orders-visibility-block ::v-deep .v-input input,
    #orders-visibility-block ::v-deep .v-label,
    #orders-visibility-block ::v-deep .v-chip {
      font-size: 13px !important;
    }
    /* Header/block toolbars: let the long title + action buttons wrap instead of
       overflowing the card on narrow screens. Both .v-toolbar and its __content must grow,
       otherwise the fixed-height toolbar overlaps the description below it. */
    #orders-visibility-block ::v-deep .v-toolbar {
      height: auto !important;
    }
    #orders-visibility-block ::v-deep .v-toolbar__content {
      height: auto !important;
      flex-wrap: wrap;
      row-gap: 4px;
      padding-top: 6px;
      padding-bottom: 6px;
    }
    #orders-visibility-block ::v-deep .v-toolbar__content .v-card__subtitle {
      font-size: 13px;
      white-space: normal;
      line-height: 1.3;
    }
  }
</style>
